import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Users, Plus, Minus, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface SeatPurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  currentSeats: number;
  onSuccess?: () => void;
}

export function SeatPurchaseModal({
  open,
  onOpenChange,
  teamId,
  currentSeats,
  onSuccess
}: SeatPurchaseModalProps) {
  const [loading, setLoading] = useState(false);
  const [seatCount, setSeatCount] = useState(currentSeats || 1);
  const { toast } = useToast();
  
  const pricePerSeat = 10;
  const monthlyPrice = seatCount * pricePerSeat;
  const isUpdating = currentSeats > 0;

  const handlePurchase = async () => {
    try {
      setLoading(true);
      
      const response = await api.post('/api/payments/purchase-team-seats', {
        seats: seatCount,
        team_id: teamId
      });
      
      // Check if we got a checkout URL or portal URL
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.portal_url) {
        // Redirect to Stripe Customer Portal for updates
        window.location.href = response.data.portal_url;
      } else {
        // Fallback success message
        toast({
          title: "Success",
          description: response.data.message || `Successfully processed seat request`,
        });
        onOpenChange(false);
        onSuccess?.();
      }
    } catch (error: any) {
      let errorMessage = 'Failed to purchase seats';
      
      // Handle different error formats
      if (error.response?.data?.detail) {
        // FastAPI error format
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation error array
          errorMessage = error.response.data.detail[0]?.msg || errorMessage;
        } else if (typeof error.response.data.detail === 'object') {
          // Single validation error object
          errorMessage = error.response.data.detail.msg || errorMessage;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const incrementSeats = () => setSeatCount(prev => prev + 1);
  const decrementSeats = () => setSeatCount(prev => Math.max(1, prev - 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {isUpdating ? 'Update Assistant Seats' : 'Purchase Assistant Seats'}
          </DialogTitle>
          <DialogDescription>
            Each assistant seat allows you to invite one team member to collaborate on your scouting platform.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <Label htmlFor="seats">Number of Assistant Seats</Label>
            <div className="flex items-center space-x-4">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={decrementSeats}
                disabled={seatCount <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              
              <Input
                id="seats"
                type="number"
                value={seatCount}
                onChange={(e) => setSeatCount(Math.max(1, parseInt(e.target.value) || 1))}
                className="text-center text-lg font-semibold w-20"
                min="1"
              />
              
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={incrementSeats}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-3 rounded-lg bg-muted p-4">
            <div className="flex justify-between text-sm">
              <span>Price per seat</span>
              <span className="font-medium">${pricePerSeat}/month</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Number of seats</span>
              <span className="font-medium">{seatCount}</span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between">
                <span className="font-medium">Total</span>
                <span className="text-lg font-bold">${monthlyPrice}/month</span>
              </div>
            </div>
          </div>

          {isUpdating && seatCount < currentSeats && (
            <div className="flex items-start gap-2 rounded-lg bg-yellow-50 p-3 text-sm">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div className="text-yellow-800">
                <p className="font-medium">Reducing seats</p>
                <p className="text-xs mt-1">
                  You're reducing from {currentSeats} to {seatCount} seats. 
                  Make sure you have {seatCount} or fewer active assistants.
                </p>
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            <p>• Seats are billed monthly</p>
            <p>• You can add or remove seats at any time</p>
            <p>• Changes take effect immediately</p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handlePurchase} disabled={loading}>
            {loading && <CreditCard className="mr-2 h-4 w-4 animate-spin" />}
            {loading 
              ? 'Processing...' 
              : isUpdating 
                ? `Update to ${seatCount} Seats` 
                : `Purchase ${seatCount} Seat${seatCount !== 1 ? 's' : ''}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}